import { diffLines } from 'diff';

/**
 * Pure line-diff → side-by-side row alignment for the prompt version diff.
 * Kept free of Angular so it is unit-testable (see prompt-diff.util.spec.ts).
 */

/** One rendered row of the side-by-side view. `null` = no line on that side. */
export interface DiffRow {
  left: string | null;
  right: string | null;
  /** same | changed (both sides differ) | removed (left only) | added (right only) */
  type: 'same' | 'changed' | 'removed' | 'added';
}

/** A run of consecutive same-rows collapsed behind a "N unchanged lines" toggle. */
export interface DiffBlock {
  rows: DiffRow[];
  collapsible: boolean;
}

/** Split a diff part's text into lines, dropping the trailing '' a final \n produces. */
function toLines(value: string): string[] {
  const lines = value.split('\n');
  if (lines.length && lines[lines.length - 1] === '') {
    lines.pop();
  }
  return lines;
}

/**
 * Align old and new text into side-by-side rows: unchanged lines appear on both
 * sides, a removed block followed by an added block is zipped into `changed`
 * rows (leftovers become one-sided rows), lone blocks stay one-sided.
 */
export function alignDiffRows(oldText: string, newText: string): DiffRow[] {
  const rows: DiffRow[] = [];
  let pendingRemoved: string[] = [];

  const flushRemoved = (addedLines: string[] | null): void => {
    const added = addedLines ?? [];
    const paired = Math.min(pendingRemoved.length, added.length);
    for (let i = 0; i < paired; i++) {
      rows.push({ left: pendingRemoved[i], right: added[i], type: 'changed' });
    }
    for (let i = paired; i < pendingRemoved.length; i++) {
      rows.push({ left: pendingRemoved[i], right: null, type: 'removed' });
    }
    for (let i = paired; i < added.length; i++) {
      rows.push({ left: null, right: added[i], type: 'added' });
    }
    pendingRemoved = [];
  };

  for (const part of diffLines(oldText ?? '', newText ?? '')) {
    const lines = toLines(part.value);
    if (part.removed) {
      pendingRemoved.push(...lines);
    } else if (part.added) {
      flushRemoved(lines);
    } else {
      flushRemoved(null);
      for (const line of lines) {
        rows.push({ left: line, right: line, type: 'same' });
      }
    }
  }
  flushRemoved(null);
  return rows;
}

/** Unchanged runs longer than this collapse behind a toggle (context stays visible). */
const COLLAPSE_OVER = 8;
const CONTEXT_LINES = 3;

/**
 * Group rows into blocks, folding long unchanged runs (prompts run to thousands
 * of lines) while keeping a few context lines around each change.
 */
export function toDiffBlocks(rows: DiffRow[]): DiffBlock[] {
  const blocks: DiffBlock[] = [];
  let sameRun: DiffRow[] = [];

  const flushSame = (isLast: boolean): void => {
    if (!sameRun.length) return;
    const leadContext = blocks.length ? CONTEXT_LINES : 0;
    const tailContext = isLast ? 0 : CONTEXT_LINES;
    if (sameRun.length > COLLAPSE_OVER + leadContext + tailContext) {
      if (leadContext) blocks.push({ rows: sameRun.slice(0, leadContext), collapsible: false });
      blocks.push({
        rows: sameRun.slice(leadContext, sameRun.length - tailContext || undefined),
        collapsible: true,
      });
      if (tailContext) blocks.push({ rows: sameRun.slice(sameRun.length - tailContext), collapsible: false });
    } else {
      blocks.push({ rows: sameRun, collapsible: false });
    }
    sameRun = [];
  };

  for (const row of rows) {
    if (row.type === 'same') {
      sameRun.push(row);
    } else {
      flushSame(false);
      const last = blocks[blocks.length - 1];
      if (last && !last.collapsible && last.rows[0]?.type !== 'same') {
        last.rows.push(row);
      } else {
        blocks.push({ rows: [row], collapsible: false });
      }
    }
  }
  flushSame(true);
  return blocks;
}
